// src/context/AuthContext.tsx
import React, { createContext, useState, useEffect, useContext, ReactNode, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { Session, User } from '@supabase/supabase-js';

// Define the shape of the profile data
export interface Profile {
  id: string;
  is_super_admin?: boolean;
  role?: string | null;
  full_name?: string | null;
  company_name?: string | null;
  industry?: string | null;
  number_of_employees?: number | null;
  primary_naics_code?: string | null; // Renamed from naics_code for clarity if needed
  secondary_naics_code?: string | null;
  street?: string | null; // Added address fields
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  phone?: string | null; // Added phone
  organization_id?: string | null; // Added organization_id
  // Notification Preferences
  notify_email_incident_report?: boolean | null;
  notify_email_assigned_ca_reviewer?: boolean | null;
}

// Define the shape of the context value
interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isSuperAdmin: boolean;
  isConnectClient: boolean;
  hasSafetyModules: boolean;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  viewingOrganizationId: string | null;
  isImpersonating: boolean;
  switchToOrganization: (orgId: string) => void;
  clearOrganizationView: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewingOrganizationId, setViewingOrganizationId] = useState<string | null>(() => sessionStorage.getItem('viewingOrganizationId'));
  const [viewedOrganization, setViewedOrganization] = useState<any>(null);

  // Track profile user ID to prevent unnecessary refetches
  const profileUserIdRef = useRef<string | null>(null);

  const isImpersonating = viewingOrganizationId !== null;

  const switchToOrganization = (orgId: string) => {
    if (profile?.is_super_admin) {
      sessionStorage.setItem('viewingOrganizationId', orgId);
      setViewingOrganizationId(orgId);
    } else {
      console.warn('[AuthContext] Attempted to switch organization without super admin privileges.');
    }
  };

  const clearOrganizationView = () => {
    sessionStorage.removeItem('viewingOrganizationId');
    setViewingOrganizationId(null);
  };

  // --- Profile Fetching Logic ---
  const fetchProfile = useCallback(async (userId: string | undefined) => {
    if (!userId) return null;
    try {
      let { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*, organizations (stripe_subscription_id, stripe_subscription_status, plan_type, is_active, is_connect_client)')
        .eq('id', userId)
        .single();
      if (profileError && profileError.code !== 'PGRST116') {
        console.error("[AuthContext] Error fetching profile:", profileError);
        // Continue to check super admin status even if profile fails
      }

      // On a brand-new signup, SignUpForm creates the profiles row only after
      // signUp() resolves, so this SELECT can race ahead of the INSERT and find
      // zero rows (PGRST116). Retry briefly before falling back to an empty
      // profile, which would otherwise pin the user at the paywall until re-login.
      for (let attempt = 0; !profileData && attempt < 4; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 400));
        const { data: retryData } = await supabase
          .from('profiles')
          .select('*, organizations (stripe_subscription_id, stripe_subscription_status, plan_type, is_active, is_connect_client)')
          .eq('id', userId)
          .single();
        profileData = retryData;
      }

      const { data: superAdminData, error: superAdminError } = await supabase
        .from('super_admins')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (superAdminError && superAdminError.code !== 'PGRST116') {
        console.error("[AuthContext] Error fetching super admin status:", superAdminError);
      }

      const isSuperAdmin = !!superAdminData;
      const result = {
        ...profileData,
        is_super_admin: isSuperAdmin,
      } as Profile;
      
      // Update ref only when a real row was found. If the row was still missing,
      // leave the ref unset so the next auth event refetches (self-healing).
      if (result.id) {
        profileUserIdRef.current = result.id;
      }

      return result;
    } catch (error: any) {
      console.error("!!! Critical Error fetching profile:", error);
      // Even if profile fails, try to check super admin status
      try {
        const { data: superAdminData } = await supabase
          .from('super_admins')
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();
        
        if (superAdminData) {
          const result = {
            id: userId,
            is_super_admin: true,
          } as Profile;
          // Update ref even for fallback profile
          profileUserIdRef.current = userId;
          return result;
        }
      } catch (superAdminError) {
        console.error('[AuthContext] Fallback super admin check also failed:', superAdminError);
      }
      return null;
    }
  }, []);

  // --- Initial Load Effect ---
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    supabase.auth.getSession().then(async ({ data: { session: initialSession }, error: sessionError }) => {
      if (!isMounted) return;

      if (sessionError) {
        console.error("!!! Critical Error fetching initial session:", sessionError);
        setSession(null); setUser(null); setProfile(null);
      } else {
        const currentUser = initialSession?.user ?? null;
        setSession(initialSession);
        setUser(currentUser);
        if (currentUser) {
          // Skip session creation for now - focus on profile loading
          try {
            const { data: sessionRecord, error: sessionInsertError } = await supabase
              .from('sessions')
              .insert({ user_id: currentUser.id });

            if (sessionInsertError) {
              console.warn('Error creating session record:', sessionInsertError.message);
              // Continue anyway - this shouldn't block login
            } else {
              await supabase.functions.invoke('session-enforcement', {
                body: { userId: currentUser.id },
              }).catch(err => {
                console.warn('Session enforcement failed:', err);
                // Continue anyway
              });
            }
          } catch (err) {
            console.warn('Session setup failed, continuing anyway:', err);
          }

          const fetchedProfile = await fetchProfile(currentUser.id);
          if (isMounted) {
            setProfile(fetchedProfile);
            setLoading(false);
          }
        } else {
          if (isMounted) {
            setProfile(null);
            setLoading(false);
          }
        }
      }
    }).catch(error => {
        console.error("!!! Catch block error during initial data fetch:", error);
        if (isMounted) { 
          setSession(null); 
          setUser(null); 
          setProfile(null);
          setLoading(false);
        }
    });
    // Remove the .finally() block - loading is now set false only when profile is complete

    // --- Auth State Change Listener (Simplified - No profile fetch) ---
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (!isMounted) return;

        const currentUser = newSession?.user ?? null;
        setSession(newSession);
        setUser(currentUser);

        // Fetch profile when user logs in or session refreshes
        if (currentUser) {
            // Only refetch if we don't already have this user's profile loaded
            if (profileUserIdRef.current !== currentUser.id) {
                // Create a sessions row on a genuine new sign-in so first-time
                // cold logins (no persisted session) are not evicted by
                // useSessionEnforcement's >=1-row check. Mirrors the boot-time
                // getSession() path. Gated on SIGNED_IN + the ref guard so it
                // never fires on TOKEN_REFRESHED, INITIAL_SESSION, or tab refocus.
                // Best-effort and fire-and-forget — never blocks login.
                if (_event === 'SIGNED_IN') {
                    (async () => {
                        try {
                            const { error: sessionInsertError } = await supabase
                                .from('sessions')
                                .insert({ user_id: currentUser.id });

                            if (sessionInsertError) {
                                console.warn('Error creating session record:', sessionInsertError.message);
                            } else {
                                await supabase.functions.invoke('session-enforcement', {
                                    body: { userId: currentUser.id },
                                }).catch(err => {
                                    console.warn('Session enforcement failed:', err);
                                });
                            }
                        } catch (err) {
                            console.warn('Session setup failed, continuing anyway:', err);
                        }
                    })();
                }

                setLoading(true); // Set loading while fetching profile
                fetchProfile(currentUser.id).then(fetchedProfile => {
                    if (isMounted) {
                        setProfile(fetchedProfile); // Set the state
                        setLoading(false); // Clear loading after fetch
                    }
                }).catch(error => {
                   console.error("!!! Error fetching profile on auth change:", error);
                   if (isMounted) {
                       setProfile(null);
                       setLoading(false); // Clear loading even on error
                   }
               });
            } else {
                // Same user, just token refresh - skip profile refetch
                // Don't set loading or refetch profile - existing profile data is still valid
            }
        } else {
            // User logged out
            profileUserIdRef.current = null;
            setProfile(null);
            setLoading(false); // Clear loading on logout
        }
      }
    );

    // Cleanup function
    return () => {
      isMounted = false;
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, [fetchProfile]); // Only fetchProfile dependency

  // Fetch the viewed org's entitlement flags when Super Admin impersonates via viewingOrganizationId.
  // Keeps isConnectClient/hasSafetyModules in sync with the effectiveOrgId pattern.
  useEffect(() => {
    if (!viewingOrganizationId) {
      setViewedOrganization(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('stripe_subscription_id, stripe_subscription_status, plan_type, is_active, is_connect_client')
        .eq('id', viewingOrganizationId)
        .single();
      if (cancelled) return;
      if (error) {
        console.error('[AuthContext] Error fetching viewed organization:', error);
        setViewedOrganization(null);
      } else {
        setViewedOrganization(data);
      }
    })();
    return () => { cancelled = true; };
  }, [viewingOrganizationId]);

  // --- Manual Logout ---
  const logout = async () => {
    await supabase.auth.signOut({ scope: 'local' });
  };

  // --- Manual Profile Refresh ---
  const refreshProfile = useCallback(async () => {
    if (user && !loading) {
      setLoading(true);
      const fetchedProfile = await fetchProfile(user.id);
      setProfile(fetchedProfile);
      setLoading(false);
    }
  }, [user, loading, fetchProfile]);

  // --- Context Value ---
  const isSuperAdmin = profile?.is_super_admin || false;

  // Mirror of SubscriptionGuard lines 29-34 — kept intentionally duplicated so SubscriptionGuard stays untouchable.
  // When Super Admin impersonates via the org switcher, read the VIEWED org's flags so the sidebar
  // matches what the impersonated org would see (effectiveOrgId pattern parity).
  const ownOrganization = (profile as any)?.organizations;
  const isImpersonatingView = isSuperAdmin && !!viewingOrganizationId && !!viewedOrganization;
  const organization = isImpersonatingView ? viewedOrganization : ownOrganization;
  const hasStripeSubscription = !!profile?.organization_id && !!organization?.stripe_subscription_id &&
    (organization?.stripe_subscription_status === 'active' || organization?.stripe_subscription_status === 'trialing');
  const isEnterprisePlan = !!profile?.organization_id && organization?.plan_type === 'enterprise';
  const isActiveOrg = organization?.is_active !== false;
  const hasSafetyModules = (hasStripeSubscription || isEnterprisePlan) && isActiveOrg;
  const isConnectClient = organization?.is_connect_client === true;

  const value = {
    session,
    user,
    profile,
    loading,
    logout,
    refreshProfile,
    isSuperAdmin,
    isConnectClient,
    hasSafetyModules,
    viewingOrganizationId,
    isImpersonating,
    switchToOrganization,
    clearOrganizationView,
  };

   return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// --- Custom Hook ---
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
